<template>
  <component
    :is="baseComponent"
    :to="link.path ? link.path : '/'"
    :class="{ active: isActive }"
    tag="li"
  >
    <a
      v-if="isMenu"
      href="#"
      class="nav-link sidebar-menu-item"
      :aria-expanded="!collapsed"
      data-toggle="collapse"
      @click.prevent="collapseMenu"
    >
      <small><i class="fas" :class="'fa-' + (link.icon || 'wrench')"></i></small>
      <p>
        {{ link.name }}
        <b class="caret"></b>
      </p>
    </a>

    <collapse-transition>
      <div v-if="$slots.default || this.isMenu" v-show="!collapsed">
        <ul class="nav">
          <slot></slot>
        </ul>
      </div>
    </collapse-transition>

    <slot
      name="title"
      v-if="children.length === 0 && !$slots.default && link.path"
    >
      <a
        v-if="isExternalLeaf"
        :href="link.path"
        :target="externalLeafTarget"
        :rel="externalLeafRel"
        class="nav-link my-0 py-1"
        @click="externalLeafClick"
      >
        <small><i class="fas" :class="'fa-' + (link.icon || 'link')"></i></small>
        <p>{{ link.name }}</p>
      </a>
      <component
        v-else
        @click.prevent="linkClick(link.path)"
        :is="elementType(link, false)"
        :class="{
          active: link.active,
          'sidebar-link-has-mention': link.mentionBadge > 0,
          'sidebar-link-locked': link.locked,
        }"
        class="nav-link my-0 py-1"
        :target="link.target"
      >
        <template>
          <small><i class="fas" :class="'fa-'+link.icon"></i></small>
          <!-- Con el menú cerrado el texto no se ve: queda el número sobre el icono -->
          <span
            v-if="link.mentionBadge > 0"
            class="sidebar-mention-dot"
            aria-hidden="true"
            >{{ link.mentionBadge > 99 ? "99+" : link.mentionBadge }}</span
          >
          <p :title="link.locked ? link.lockedTitle : null">
            {{ link.name }}
            <!-- SVG y no <i>: las reglas del sidebar para <i> lo desacomodan -->
            <svg
              v-if="link.locked"
              class="sidebar-lock"
              viewBox="0 0 16 16"
              width="11"
              height="11"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 15h8a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12 7h-.5V4.5A3.5 3.5 0 0 0 8 1zm2 6H6V4.5a2 2 0 1 1 4 0V7z"
              />
            </svg>
            <span
              v-if="link.mentionBadge > 0"
              class="sidebar-mention-badge"
              :title="$t('Unread mentions')"
            >
              <span class="sidebar-mention-badge-icon" aria-hidden="true">
                <!-- SVG y no <i>: las reglas del sidebar para <i> lo desacomodan -->
                <svg viewBox="0 0 16 16" width="10" height="10">
                  <path
                    fill="currentColor"
                    d="M8 1.5C4.13 1.5 1 4.02 1 7.13c0 1.6.83 3.04 2.17 4.07L2.5 14.5l3.37-1.69c.68.17 1.39.26 2.13.26 3.87 0 7-2.52 7-5.94S11.87 1.5 8 1.5z"
                  />
                </svg>
              </span>
              <span class="sidebar-mention-badge-count">{{
                link.mentionBadge
              }}</span>
            </span>
          </p>
        </template>
      </component>
    </slot>
  </component>
</template>
<script>
import { CollapseTransition } from "vue2-transitions";

export default {
  name: "sidebar-item",
  components: {
    CollapseTransition,
  },
  props: {
    menu: {
      type: Boolean,
      default: false,
    },
    link: {
      type: Object,
      default: () => {
        return {
          name: "",
          path: "",
          disableCollapse: Boolean,
          children: [],
        };
      },
    },
  },
  provide() {
    return {
      addLink: this.addChild,
      removeLink: this.removeChild,
    };
  },
  inject: {
    addLink: { default: null },
    removeLink: { default: null },
    autoClose: {
      default: true,
    },
  },
  data() {
    return {
      children: [],
      collapsed: true,
      mercadopago: process.env.BASE_URL + "img/mercado-pago.png",
    };
  },
  computed: {
    baseComponent() {
      if (this.isMenu || this.link.isRoute) return "li";
      if (this.isExternalLeaf) return "li";
      return "router-link";
    },
    linkPrefix() {
      if (this.link.name) {
        let words = this.link.name.split(" ");
        return words.map((word) => word.substring(0, 1)).join("");
      }
      return false;
    },
    isMenu() {
      return this.children.length > 0 || this.menu === true;
    },
    isActive() {
      if (this.$route && this.$route.path) {
        let matchingRoute = this.children.find((c) =>
          this.$route.path.startsWith(c.link.path)
        );
        if (matchingRoute !== undefined) {
          return true;
        }
      }
      return false;
    },
    /** http(s), mailto, tel — native anchor, not vue-router */
    isExternalLeaf() {
      if (this.menu || this.isMenu) return false;
      if (!this.link || !this.link.path) return false;
      const p = this.link.path;
      return (
        /^https?:\/\//i.test(p) ||
        /^mailto:/i.test(p) ||
        /^tel:/i.test(p)
      );
    },
    externalLeafTarget() {
      if (!this.link || !this.link.path) return null;
      const p = this.link.path;
      if (/^mailto:/i.test(p) || /^tel:/i.test(p)) return null;
      return "_blank";
    },
    externalLeafRel() {
      return this.externalLeafTarget === "_blank" ? "noopener noreferrer" : null;
    },
  },
  methods: {
    addChild(item) {
      const index = this.$slots.default.indexOf(item.$vnode);
      this.children.splice(index, 0, item);
    },
    removeChild(item) {
      const tabs = this.children;
      const index = tabs.indexOf(item);
      tabs.splice(index, 1);
    },
    elementType(link, isParent = true) {
      if (link.isRoute === false) {
        return isParent ? "li" : "a";
      } else {
        return "a";
      }
    },
    linkAbbreviation(name) {
      const matches = name.match(/\b(\w)/g);
      return matches.join("");
    },
    externalLeafClick() {
      if (!this.addLink && !this.link.disableCollapse) {
        this.$sidebar.collapseAllMenus();
      }
      if (this.autoClose && this.$sidebar && this.$sidebar.isExpanded) {
        this.$sidebar.toggleExpanded();
      }
      if (
        this.autoClose &&
        this.$sidebar &&
        this.$sidebar.showSidebar === true
      ) {
        this.$sidebar.displaySidebar(false);
      }
    },
    linkClick(url) {
      if (!this.addLink && !this.link.disableCollapse) {
        this.$sidebar.collapseAllMenus();
      }

      // Cerrar sidebar al hacer clic en un item que no es submenú
      if (this.autoClose && this.$sidebar) {
        // Solo cerrar si no es un menú con subitems y el sidebar está expandido
        if (!this.isMenu && this.$sidebar.isExpanded) {
          this.$sidebar.toggleExpanded();
        }
      }

      if (
        this.autoClose &&
        this.$sidebar &&
        this.$sidebar.showSidebar === true
      ) {
        this.$sidebar.displaySidebar(false);
      }

      // Si navegamos a la misma ruta pero sin query params, usar replace para forzar el cambio
      const currentPath = this.$route.path;
      const targetPath = url.split('?')[0];
      const hasCurrentQuery = Object.keys(this.$route.query).length > 0;
      const targetHasNoQuery = !url.includes('?');
      
      if (currentPath === targetPath && hasCurrentQuery && targetHasNoQuery) {
        this.$router.replace({ path: url, query: {} }).catch(() => {});
      } else {
        this.$router.push(url).catch(() => {});
      }
      return;
    },
    collapseMenu() {
      if (this.collapsed && !this.link.disableCollapse) {
        this.$sidebar.addSidebarLink(this);
        // Acordeón solo entre ítems del mismo “nivel” raíz del sidebar. Si hay
        // inject addLink, este ítem está dentro de otro sidebar-item (carpeta,
        // modelo con hijos, etc.): no cerrar padres al abrir el hijo (evita que
        // al abrir All/Add new dentro de un modelo en una carpeta se colapse la carpeta).
        if (!this.addLink) {
          this.$sidebar.collapseAllMenus();
        }
      }

      this.collapsed = !this.collapsed;
    },
    collapseSubMenu(link) {
      link.collapsed = !link.collapsed;
    },
  },
  mounted() {
    if (this.addLink) {
      this.addLink(this);
    }
    if (this.link.collapsed !== undefined) {
      this.collapsed = this.link.collapsed;
    }
    if (this.isActive && this.isMenu) {
      this.collapsed = false;
    }
  },
  destroyed() {
    this.$sidebar.removeSidebarLink(this);
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
    if (this.removeLink) {
      this.removeLink(this);
    }
  },
};
</script>
<style>
.sidebar-menu-item {
  cursor: pointer;
}
.sidebar .nav li a {
  color: white !important;
}
.sidebar .nav li a:hover {
  color: white !important;
}
/* Mismo badge rojo que el listado de soporte (menciones sin leer) */
.sidebar-mention-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  margin-left: 0.35rem;
  vertical-align: middle;
}
.sidebar .nav .sidebar-mention-badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background: #dc3545;
  color: #fff;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.22);
  animation: sidebar-mention-titilar 1.6s ease-in-out infinite;
}
.sidebar .nav .sidebar-mention-badge-icon svg {
  display: block;
}
.sidebar-mention-badge-count {
  font-size: 0.8rem;
  font-weight: 700;
  color: #ff6b78;
  line-height: 1;
}
.sidebar-mention-dot {
  display: none;
}
/* Reporte privado: candado a la derecha, dentro del bloque del hover. Va en el <p>,
   así que con el menú cerrado se oculta junto con el nombre. */
.sidebar .nav .sidebar-link-locked {
  position: relative;
}
.sidebar .nav .sidebar-link-locked p {
  padding-right: 1.6rem;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar .nav .sidebar-lock {
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
  color: inherit;
}
.sidebar .nav .sidebar-link-has-mention {
  position: relative;
}
body.sidebar-mini .sidebar .nav .sidebar-mention-dot {
  display: block;
  position: absolute;
  top: 2px;
  left: 34px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  z-index: 2;
  pointer-events: none;
}
@keyframes sidebar-mention-titilar {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.92);
  }
}
</style>
<style scoped>
.subitem .nav-link i {
  font-size: 16px !important;
  margin-top: -3px;
}
</style>
